import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { onboardStyles } from '../../components/ComponentStyles';
import { useJWT } from '../../contexts/JWTProvider';
import { ServiceThemeOptions } from '../../themes';
import { Analytics, createAnalyticsContext } from '../../utils/analytics';

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
  helpText: React.ReactNode;
  tsp: string;
  serviceName: string;
}

/**
 * A modal component for displaying help text for TSP credentials
 */
const HelpModal: React.FC<HelpModalProps> = ({
  open,
  onClose,
  helpText,
  tsp,
  serviceName,
}) => {
  const theme = useTheme() as ServiceThemeOptions;
  const jwt = useJWT();

  // Track help modal events
  React.useEffect(() => {
    if (open) {
      amplitude.track('Help Modal Opened', { tsp, serviceName });

      // Track help request through analytics system
      const analyticsContext = createAnalyticsContext(jwt, serviceName, {
        tsp,
      });
      Analytics.helpRequestedCredentials(analyticsContext, 'Onboard');
    }
  }, [open, tsp, serviceName, jwt]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='modal-help-title'
      aria-describedby='modal-help-description'
    >
      <Box sx={onboardStyles.helpModalStyle(theme as any)}>
        <IconButton
          aria-label='close'
          sx={{ position: 'absolute', top: 8, right: 8 }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
        <Typography id='modal-help-title' variant='h6' component='h2'>
          Need help finding your credentials?
        </Typography>
        <Box id='modal-help-description' sx={{ mt: 2 }}>
          {helpText}
        </Box>
      </Box>
    </Modal>
  );
};

export default HelpModal;
